import * as Sentry from '@sentry/react';

interface SentryFrontendConfig {
  dsn: string;
  apiEndpoint: string;
  stage: string;
  version?: string;
}

/**
 * Client-side rate limiter to prevent flooding Sentry and hitting 429s.
 * Uses a sliding window (1 minute) to cap events sent from each browser tab.
 */
const rateLimiter = {
  errors: { count: 0, windowStart: 0 },
  transactions: { count: 0, windowStart: 0 },
};

const MAX_ERRORS_PER_MINUTE = 25;
const MAX_TRANSACTIONS_PER_MINUTE = 50;
const WINDOW_MS = 60_000;

function isWithinRateLimit(bucket: { count: number; windowStart: number }, max: number): boolean {
  const now = Date.now();
  if (now - bucket.windowStart > WINDOW_MS) {
    bucket.count = 0;
    bucket.windowStart = now;
  }
  bucket.count++;
  return bucket.count <= max;
}

export function initSentryFrontend(config: SentryFrontendConfig) {
  // if (config.stage === 'local') {
  //   return; // Don't initialize Sentry for local development
  // }

  Sentry.init({
    release: config.version,
    dsn: config.dsn,
    environment: config.stage,

    // Enable distributed tracing to backend
    tracePropagationTargets: [config.apiEndpoint],

    integrations: [
      Sentry.browserTracingIntegration({
        // Filter out unwanted third-party HTTP requests
        shouldCreateSpanForRequest: url => {
          // Allow our own API calls for distributed tracing
          if (url.includes(config.apiEndpoint)) {
            return true;
          }

          // Block specific third-party services that create noise
          const blockedDomains = [
            'fast.trychameleon.com',
            'fast.chmln-cdn.com',
            'www.loom.com',
            'loom.com',
            'trychameleon.com',
            'chmln-cdn.com',
          ];

          return !blockedDomains.some(domain => url.includes(domain));
        },
      }),
      // CRITICAL: DO NOT use replayIntegration for HIPAA compliance
      // Session replay can capture PHI even with masking enabled
      // Sentry.replayIntegration(), // REMOVED FOR HIPAA COMPLIANCE
    ],

    // Drop browser PerformanceResourceTiming spans — `resource.script`,
    // `resource.link`, `resource.img`, `resource.css`, etc. They accounted
    // for ~470k spans/day (the largest single source of frontend volume)
    // and aren't actionable without per-trace investigation.
    // Navigation, pageload, and long-animation-frame spans are preserved.
    ignoreSpans: [{ op: /^resource\./ }],

    // Performance sampling — traces a portion of transactions for performance monitoring.
    // 20% gives good visibility without hitting rate limits.
    tracesSampleRate: config.stage === 'prod' ? 0.2 : 0.0,
    replaysSessionSampleRate: config.stage === 'prod' ? 0.01 : 0.0, // Only replay in production
    replaysOnErrorSampleRate: 1.0, // Always capture errors with replay (all environments)

    // Client-side rate limiting to prevent 429s from Sentry.
    // Drops events once a per-tab threshold is exceeded within a sliding 1-minute window.
    beforeSend(event) {
      if (!isWithinRateLimit(rateLimiter.errors, MAX_ERRORS_PER_MINUTE)) {
        return null; // drop the event
      }
      // Add current route for easier error triage
      event.tags = {
        ...event.tags,
        route: window.location.pathname,
      };
      return event;
    },

    beforeSendTransaction(event) {
      if (!isWithinRateLimit(rateLimiter.transactions, MAX_TRANSACTIONS_PER_MINUTE)) {
        return null; // drop the transaction
      }
      return event;
    },

    // Common error filtering
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook issues
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // Conduit toolbar
      'conduitPage',
      // Chameleon script errors
      "Can't find variable: EmptyRanges",
      // Network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'A network error has occurred',
      // ResizeObserver errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Chrome extensions / unhandled API error objects (no stacktrace, not actionable)
      'Non-Error promise rejection captured',
      'Object captured as promise rejection with keys',
      'captured as promise rejection',
      // Script errors from CDNs
      'Script error',
      // Stale tabs after deploys — user has an old JS bundle cached
      'ChunkLoadError',
      'Loading chunk',
      'Importing a module script failed',
      'dynamically imported module',
      // User navigated away or cancelled a request mid-flight
      'AbortError',
      'The operation was aborted',
      'signal is aborted without reason',
      'The user aborted a request',
      // Browser permission prompts (clipboard, notifications, etc.)
      'NotAllowedError',
      // Safari-specific benign errors
      'The request is not allowed by the user agent',
      // Cognito/auth token refresh noise
      'No current user',
      'The user is not authenticated',
      "Logins don't match",
      // Benign AudioContext cleanup errors from WaveSurfer during navigation
      'Cannot close a closed AudioContext',
      "Can't close an AudioContext twice",
      // Benign visibility/focus errors
      'Document is not focused',
      // React DOM reconciliation errors caused by browser extensions/translation
      // tools mutating the DOM outside React's control (no first-party frames)
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'removeChild' on 'Node'",
      // FingerprintJS agent CDN blocked by tracking protection / ad blockers.
      // Device fingerprinting is best-effort and fails open (backend abuse gate
      // proceeds without it), so a blocked agent script is not actionable.
      'Failed to load the JS script of the agent',
    ],

    denyUrls: [
      // react-custom-scrollbars-2: unmaintained lib calls document.body.appendChild
      // during resize when document.body is null (no first-party frames)
      /react-custom-scrollbars-2/i,
      // signature_pad@2.3.2: _strokeUpdate/_strokeEnd crash when _data is empty
      // (mobile touch edge cases). No stable upgrade path available.
      /signature_pad/i,
      // Facebook
      /graph\.facebook\.com/i,
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i,
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /fast\.trychameleon\.com/i,
      // Local development
      /localhost:\d+/i,
      /127\.0\.0\.1/i,
    ],
  });
}
